import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IObjectData, IRouteDefinitionParams, ISyncField, TSyncfield} from "../../../index";
import {global} from "../../../appContext";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import PromiseRequest from "../../../req/PromiseRequest";
import {nsRoute} from "../../../util/route";
import {PositionModel} from "./PositionModel";
import {PersonaliaModel} from "./PersonaliaModel";
import {nsUserWizard} from "./nsUserWizard";
import {getObjectDisplayName, getObjectType, getRawSyncFieldValue} from "../../../util/misc";
import {IDataset, TOperationType} from "./index";
import {INPUT_DATE_FORMAT, MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../constants";
import {convertFromServerString, convertToServerString} from "../../../util/datetime";
import {storeObservable} from "../../../util/sessionstore";
import {ButtonManager} from "../../../util/ButtonManager";
import {
    getCancelButton,
    getDiscardButton,
    getExitButton,
    getHash,
    getNextButton,
    getPreviousButton,
    getSubmitButton
} from "../../../constants/RouteDefinitions";
import {searchObjectWithSyncfields} from "../../../util/request";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {moment} from 'moment';
import {TSyncfield_User} from "../../../constants/Syncfields";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import TWizardSyncfield = nsUserWizard.TWizardSyncfield;
import Step = nsUserWizard.Step;
import ButtonOrder = nsRoute.ButtonOrder;
import getSourceToTargetTuples = nsSyncFields.getSourceToTargetTuples;
import TPositionType = nsUserWizard.TPositionType;
import mutableHtml from './mutable.html?raw';

export let template = mutableHtml;

export abstract class UserWizardMutable extends ParsedRouteDataDecorator {

    protected readonly Step = Step;
    protected readonly step: KnockoutObservable<Step | null> = ko.observable(null);

    protected readonly stepHeadings: Record<Step, string> = {
        [Step.Personalia]: 'personal data',
        [Step.PrimaryPosition]: 'primary position',
        [Step.SecondaryPositions]: 'secondary position|p',
        [Step.Summary]: 'summary',
        [Step.Processing]: 'processing|td',
        [Step.Success]: 'completed',
        [Step.Error]: 'error',
    }
    protected readonly heading: KnockoutObservable<string | null> = ko.observable(null);

    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected readonly isReady: KnockoutObservable<boolean> = ko.observable(false);

    protected abstract reqSubmit: PromiseRequest;
    protected abstract operationType: TOperationType

    protected readonly dataset: IDataset = {
        departments: null,
        original: null,
        syncfields: ko.observable(null),
        usedValues: null, // to be populated at init()
    }

    protected personaliaModel!: PersonaliaModel
    protected primaryPositionModel!: PositionModel
    protected secondaryPositionModels!: KnockoutObservableArray<PositionModel>
    private pageButtons: ButtonManager = new ButtonManager()
    protected optionalSyncfields!: KnockoutObservableArray<ISyncField>

    constructor(params: IRouteDefinitionParams) {
        super(params);

        const cancelBtn = getCancelButton(undefined, {
            validator: () => !this.isStep(Step.Success, Step.Error),
        })
        if (cancelBtn) {
            this.pageButtons.add(cancelBtn)
        }
        this.pageButtons.add(getPreviousButton({
            onClick: this.prevStepClick.bind(this),
            enabled: () => !this.isStep(Step.Personalia),
            validator: () => !this.isStep(Step.Success, Step.Error),
        }))
        this.pageButtons.add(getNextButton({
            onClick: this.nextStepClick.bind(this),
            enabled: () => this.isStep(Step.Personalia, Step.PrimaryPosition, Step.SecondaryPositions) && this.isValid(),
            validator: () => !this.isStep(Step.Success, Step.Error),
        }))
        this.pageButtons.add(getSubmitButton({
            onClick: this.nextStepClick.bind(this),
            enabled: this.isValid.bind(this),
            validator: () => this.isStep(Step.Summary),
        }))
        this.pageButtons.add(getDiscardButton({
            onClick: this.init.bind(this),
            label: 'start over',
            order: ButtonOrder.START_OVER,
            validator: () => this.isStep(Step.Success),
        }))
        this.pageButtons.add({
            cssClass: 'primary action',
            icon: 'fa-manual-user',
            label: '((see details)) ((manual source data|ac))',
            order: ButtonOrder.DETAILS,
            href: () => getHash('manualuserdetails', {id: this.dataset.original?.id ?? ''}) ?? '',
            validator: () => this.isStep(Step.Success),
        })
        const exitBtn = getExitButton(undefined, {
            validator: () => this.isStep(Step.Success, Step.Error),
        })
        if (exitBtn) {
            this.pageButtons.add(exitBtn)
        }

        this.kosub.subscribe(this.step, step => {
            switch (step) {
                case nsUserWizard.Step.Processing: {
                    this.onSubmit()
                    break;
                }
            }
        })

        this.kosub.subscribe(storeObservable('language'), () => {
            this.loadSyncfields()
        })
    }

    protected init(): Promise<any> {
        this.optionalSyncfields = ko.observableArray();
        this.primaryPositionModel = this.generatePositionModel('primary')
        this.secondaryPositionModels = ko.observableArray<PositionModel>([])

        const keys: TSyncfield[] = ['Id', 'Name', 'Department', 'DepartmentNumber', 'Manager', 'OrgUnitnr', 'ParentDepartmentNumber'];
        return Promise.resolve()
            .then(() => this.loadSyncfields())
            .then(() => {
                const keys: TSyncfield_User[] = ['SourceId', 'EmployeeNumber', 'Socialsecuritynumber'];
                return searchObjectWithSyncfields(ObjectTypeId.User, keys, {
                    resultsprpage: MAX_RESULTS_PER_PAGE,
                    onlytuples: true,
                })
                    .then((data: string[][] | IObjectData[]) => {
                        const tuples = data as string[][]
                        this.dataset.usedValues = {
                            SourceId: tuples.map(o => o[0] ?? ''),
                            EmployeeNumber: tuples.map(o => o[1] ?? ''),
                            Socialsecuritynumber: tuples.map(o => o[2] ?? ''),
                        }
                    })
            })
            .then(() => searchObjectWithSyncfields(
                    ObjectTypeId.Department,
                    keys,
                    {resultsprpage: MAX_RESULTS_PER_PAGE, onlytuples: true}
                ) as Promise<string[][]>
            )
            .then(data => {
                const objectType = getObjectType(ObjectTypeId.Department);
                this.dataset.departments = data.map((o: string[]) => {
                    const obj = nsSyncFields.convertTuplesToObjectData(objectType, keys, o);
                    const tuples = getSourceToTargetTuples({
                        srcObjectTypeId: ObjectTypeId.User,
                        trgObjectTypeId: ObjectTypeId.Department,
                        key: "DepartmentNumber",
                    })[0]
                    if (!tuples) {
                        return obj
                    }
                    const _name = getObjectDisplayName(obj);
                    const _identifier = getRawSyncFieldValue(tuples[3], obj.syncfields);
                    const name = `${_name} (${_identifier})`
                    return _.defaults({name: name}, obj)
                })
            })
    }

    private loadSyncfields() {
        const reqObj = nsSyncFields.getSyncFields(ObjectTypeId.User)
        return reqObj.request.then((data: ISyncField[]) => this.dataset.syncfields(data))
    }

    protected isStep(...steps: number[]): boolean {
        return steps.reduce((bool: boolean, step: number) => bool || step === this.step(), false);
    }

    protected nextStepClick() {
        const currentStep = this.step()
        if (currentStep == null) {
            return
        }
        this.step(currentStep + 1);
    }

    protected prevStepClick() {
        const currentStep = this.step()
        if (currentStep == null) {
            return
        }
        const step: Step = currentStep == Step.Error ? Step.SecondaryPositions : currentStep - 1;
        this.step(step);
    }

    protected isValid = ko.pureComputed((): boolean => {
        switch (this.step()) {
            case Step.Personalia:
                return this.personaliaModel.errorManager.validate()
            case Step.PrimaryPosition:
                return this.primaryPositionModel.errorManager.validate()
            case Step.SecondaryPositions:
                return this.secondaryPositionModels().every(o => o.errorManager.validate())
            default:
                return true
        }
    });

    private onAddSecondaryPositionClick() {
        const model = this.generatePositionModel('secondary')
        const start = moment().startOf('day').format(INPUT_DATE_FORMAT)
        model.initMeta({
            start,
            isNoStartDate: false,
        })
        this.optionalSyncfields().forEach(sf => {
            model.optionalSyncfieldObservables[sf.name] = ko.observable(null)
        })
        this.secondaryPositionModels.push(model)
    }

    private onRemoveSecondaryPositionClick(obj: PositionModel) {
        const index = this.secondaryPositionModels.indexOf(obj);
        this.secondaryPositionModels.remove(obj)
        global.snackbarData({
            message: `s is removed|s:((Position))`,
            undo: () => {
                this.secondaryPositionModels.splice(index, 0, obj)
            }
        });
    }

    protected getSubmitSyncfields = ko.pureComputed((): ISyncField[] => {
        if (!this.isReady()) {
            return []
        }
        const syncfields = this.dataset.syncfields()
        if (!syncfields) {
            return []
        }
        const standardSyncfields = nsUserWizard.WizardSyncfieldNames.map(syncfieldName => {
            const sf = _.clone(syncfields.find(sf => sf.name == syncfieldName))
            if (!sf) {
                throw new Error(`Missing syncfield ${syncfieldName}`)
            }
            switch (sf.name) {
                case 'GivenName':
                case 'Surname':
                case 'DisplayName':
                case 'Socialsecuritynumber':
                case 'EmployeeNumber':
                case 'SourceId':
                case 'Mobile':
                case 'PrivatePhone':
                case 'PrivateEmail':
                    const model = this.personaliaModel;
                    sf.value = (model.attr[syncfieldName] ?? model.derived[syncfieldName])?.() ?? ''
                    break
                case 'Department':
                case 'DepartmentNumber':
                case 'Manager':
                case 'Title':
                case 'WwwUrl': {
                    const model = this.primaryPositionModel;
                    sf.value = (model.derived[syncfieldName] ?? model.attr[syncfieldName])?.() ?? ''
                    break
                }
                case 'ExpiryDate':
                case 'Stop':
                    const datesArr = this.getPositionArray(syncfieldName).filter(Boolean)
                    if (datesArr.length) {
                        const max = moment.max(datesArr.map(date => convertFromServerString(date)))
                        sf.value = convertToServerString(max)
                    } else {
                        sf.value = ''
                    }
                    break
                case 'Start': {
                    const datesArr = this.getPositionArray(syncfieldName).filter(Boolean)
                    if (datesArr.length) {
                        const min = moment.min(datesArr.map(date => convertFromServerString(date)))
                        sf.value = convertToServerString(min)
                    } else {
                        sf.value = ''
                    }
                    break
                }
                case 'APositionCodeName':
                case 'APositionTypeName':
                    sf.value = this.getPositionArrayString('Title')
                    break
                case 'APositionEndDate':
                    sf.value = this.getPositionArrayString('ExpiryDate')
                    break
                case 'APositionManager':
                    sf.value = this.getPositionArrayString('Manager')
                    break
                case 'APositionStartDate':
                    sf.value = this.getPositionArrayString('Start')
                    break
                case 'APrimaryPosition':
                    sf.value = ['true', ...this.secondaryPositionModels().map(o => 'false')].join('|') + '|'
                    break
                case 'AUnitId':
                case 'ParentDepartmentNumber':
                    sf.value = this.getPositionArrayString('DepartmentNumber')
                    break
                case 'AUnitName':
                    sf.value = this.getPositionArrayString('Department')
                    break
                case 'ManualMerge':
                    sf.value = 'true'
                    break
                case 'Company':
                    sf.value = this.primaryPositionModel.attr.Company?.() ?? ''
                    break
                case 'ACompany':
                    sf.value = [this.primaryPositionModel.attr.Company?.() ?? '', ...this.secondaryPositionModels().map(model => model.attr.Company?.() ?? '')].join('|') + '|'
                    break
            }
            return sf
        })

        const models = [this.primaryPositionModel, ...this.secondaryPositionModels()]
        const optionalSyncfieldIds = this.optionalSyncfields().map(sf => sf.syncfieldid)
        const optionalSyncfields = nsUserWizard.WizardOptionalPositionSyncfieldNames.map(syncfieldName => {
            const sf = _.clone(syncfields.find(sf => sf.name == syncfieldName))
            if (!sf) {
                throw new Error(`Missing syncfield ${syncfieldName}`)
            }
            if (!optionalSyncfieldIds.includes(sf.syncfieldid)) {
                sf.value = null
                return sf
            }
            const values = models.map(model => {
                return model.optionalSyncfieldObservables[syncfieldName]?.() ?? ''
            })
            sf.value = values.join('|') + '|'
            return sf
        })

        return standardSyncfields.concat(optionalSyncfields)
    })

    private getPositionArrayString(key: TWizardSyncfield): string {
        return this.getPositionArray(key).join('|') + '|'
    }

    private getPositionArray(key: TWizardSyncfield): string[] {
        const ret: string[] = []
        const models = [this.primaryPositionModel, ...this.secondaryPositionModels()]
        models.forEach(model => {
            ret.push((model.attr[key] || model.derived[key])?.() ?? '')
        })
        return ret
    }

    protected getSubmitData(): Partial<IObjectData> {
        const orgId = global.login.selectedOrganizationId()
        return {
            ...(orgId ? {companyid: orgId, orgnr: orgId} : {}),
            id: 0,
            manual: true,
            syncfields: this.getSubmitSyncfields(),
            type: getObjectType(ObjectTypeId.User),
        };
    }

    protected abstract onSubmit(): void

    private getStepNumber(): string | null {
        const currentStep = this.step()
        if (currentStep == null) {
            return null
        }
        return currentStep <= Step.Summary
            ? `${currentStep + 1}/4`
            : null
    }

    private allPositionModels = ko.pureComputed(() => {
        return [this.primaryPositionModel, ...this.secondaryPositionModels()]
    })

    protected generatePositionModel(positionType: TPositionType): PositionModel {
        const ret = new PositionModel({
            dataset: this.dataset,
            positionType: positionType,
            optionalSyncfields: this.optionalSyncfields,
            onAddOptionalSyncfieldClick: sf => {
                this.allPositionModels().forEach(model => {
                    model.optionalSyncfieldObservables[sf.name] = ko.observable(null)
                })
                this.optionalSyncfields.push(sf)
            },
            onRemoveOptionalSyncfieldClick: sf => {
                this.allPositionModels().forEach(model => {
                    delete model.optionalSyncfieldObservables[sf.name]
                })
                this.optionalSyncfields.remove(sf)
            },
            opertationType: this.operationType
        })
        ret.initErrorObjects()
        return ret
    }

    override dispose() {
        super.dispose();
        this.personaliaModel?.dispose()
        this.primaryPositionModel?.dispose()
        this.secondaryPositionModels?.().forEach(model => model.dispose())
    }

}